/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function TradeHistoryChart({ assetData, perfByDate, totalPnl, COLORS }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 600, h: 200 });
  const [period, setPeriod] = useState("ALL");
  const [hoveredDot, setHoveredDot] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      const { width } = es[0].contentRect;
      if (width > 0) setDims(d => ({ ...d, w: width }));
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims(d => ({ ...d, w: width }));
        }
      }));
    }
    return () => obs.disconnect();
  }, []);

  // Filter by period
  const now = new Date();
  const cutoff = { "7D": 7, "30D": 30, "6M": 180, "1Y": 365 }[period];
  const filtered = cutoff
    ? perfByDate.filter(d => (now - new Date(d.date)) / 86400000 <= cutoff)
    : perfByDate;

  // Build asset color map
  const assetColorMap = {};
  [...assetData].sort((a,b) => b.currentValue - a.currentValue).forEach((a, i) => {
    assetColorMap[a.ticker] = COLORS[i % COLORS.length];
  });

  // Build trade dots grouped by date
  const tradesByDate = {};
  assetData.forEach(asset => {
    (asset.entries || []).forEach(e => {
      if (!tradesByDate[e.date]) tradesByDate[e.date] = [];
      tradesByDate[e.date].push({ ticker: asset.ticker, color: assetColorMap[asset.ticker] || C.orange, dollars: e.dollars, shares: e.shares });
    });
  });

  const fmt$ = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:0, maximumFractionDigits:0 }).format(v);
  const PAD = { t:60, r:20, b:28, l:60 };
  const W = Math.max(dims.w, 100), H = 220;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;
  const maxVal = Math.max(...filtered.map(d => d.value), 1);
  const xS = i => PAD.l + (i / Math.max(filtered.length - 1, 1)) * gW;
  const yS = v => PAD.t + gH - (v / maxVal) * gH;

  const buildPath = () => {
    if (filtered.length < 2) return "";
    let d = `M ${xS(0)} ${yS(filtered[0].value)}`;
    for (let i = 1; i < filtered.length; i++) {
      const cpx = (xS(i-1) + xS(i)) / 2;
      d += ` C ${cpx} ${yS(filtered[i-1].value)}, ${cpx} ${yS(filtered[i].value)}, ${xS(i)} ${yS(filtered[i].value)}`;
    }
    return d;
  };

  // Trade dots on filtered data
  const tradeDots = filtered.map((p, i) => {
    const trades = tradesByDate[p.date];
    if (!trades) return null;
    return { date: p.date, trades, x: xS(i), y: yS(p.value) };
  }).filter(Boolean);

  const lineColor = totalPnl >= 0 ? C.green : C.red;

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 16px 10px", marginBottom:24 }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888" }}>TRADE HISTORY</div>
        <div style={{ display:"flex", gap:4 }}>
          {["7D","30D","6M","1Y","ALL"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding:"4px 10px", borderRadius:5, cursor:"pointer",
              fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
              background: period === p ? C.orange : "transparent",
              color: period === p ? "#000" : "var(--text4)",
              border: period === p ? `1px solid ${C.orange}` : "1px solid var(--border)",
              transition:"all 0.15s",
            }}>{p}</button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div ref={wrapRef} style={{ width:"100%", position:"relative" }}>
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display:"block", overflow:"visible" }}>
          {/* Grid */}
          {[0.25,0.5,0.75,1].map(f => (
            <g key={f}>
              <line x1={PAD.l} y1={yS(maxVal*f)} x2={W-PAD.r} y2={yS(maxVal*f)} stroke="var(--border)" strokeWidth="1"/>
              <text x={PAD.l-6} y={yS(maxVal*f)+4} textAnchor="end" fill="var(--text5)" fontSize="9" fontFamily="Montserrat,sans-serif">
                {maxVal*f >= 1e6 ? `$${(maxVal*f/1e6).toFixed(1)}M` : `$${(maxVal*f/1000).toFixed(0)}k`}
              </text>
            </g>
          ))}

          {/* Value line — grey when a legend item is hovered */}
          <path d={buildPath()} fill="none" stroke={hoveredLegend ? "#333" : "#a78bfa"} strokeWidth="2" strokeLinecap="round" style={{ transition:"stroke 0.2s" }}/>

          {/* Trade dots */}
          {tradeDots.map((dot, i) => {
            const isHov = hoveredDot === i;
            const baseR = Math.min(2 + dot.trades.length * 1.2, 7);
            const r = isHov ? baseR + 1.5 : baseR;
            const firstTrade = dot.trades[0];
            const firstColor = hoveredLegend
              ? (firstTrade?.ticker === hoveredLegend ? firstTrade?.color || C.orange : "#444")
              : firstTrade?.color || C.orange;
            const multi = dot.trades.length > 1;
            return (
              <g key={i}>
                <circle cx={dot.x} cy={dot.y} r={r}
                  fill={firstColor} stroke="none"
                  style={{ cursor:"pointer" }}
                  onMouseEnter={() => setHoveredDot(i)}
                  onMouseLeave={() => setHoveredDot(null)}
                />
                {/* Multi-color dots stacked vertically — max 10 visible */}
                {/* Multi-color dots stacked vertically — max 10 visible */}
                {multi && (() => {
                  const allDollars = dot.trades.map(t => t.dollars);
                  const maxD = Math.max(...allDollars);
                  return dot.trades.slice(1, isHov ? dot.trades.length : 10).map((t, ti) => {
                    const dr = Math.max(1.5, Math.min((t.dollars / maxD) * 5, 5));
                    const dotColor = hoveredLegend ? (t.ticker === hoveredLegend ? t.color : "#444") : t.color;
                    return (
                      <circle key={ti} cx={dot.x} cy={dot.y - r - 2 - (ti+1)*(2*2+3)} r={dr}
                        fill={dotColor} stroke="none"
                        style={{ cursor:"pointer" }}
                        onMouseEnter={() => setHoveredDot(i)}
                        onMouseLeave={() => setHoveredDot(null)}
                      />
                    );
                  });
                })()}
                {/* "+N more" indicator if > 10 and not hovered */}
                {!isHov && dot.trades.length > 10 && (
                  <text x={dot.x} y={dot.y - r - 2 - 10*(7) - 6} textAnchor="middle"
                    fill="var(--text5)" fontSize="7" fontFamily="Montserrat,sans-serif">
                    +{dot.trades.length - 10}
                  </text>
                )}

                {/* Tooltip balloon — always above */}
                {isHov && (() => {
                  const lineH = 22;
                  const tipH = 28 + dot.trades.length * lineH;
                  const tipW = 210;
                  const tipX = Math.min(Math.max(dot.x - tipW/2, PAD.l), W - PAD.r - tipW);
                  const tipY = dot.y - tipH - 14;
                  return (
                    <g>
                      {/* Shadow */}
                      <rect x={tipX+2} y={tipY+2} width={tipW} height={tipH} rx="8" fill="rgba(0,0,0,0.3)"/>
                      <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="8"
                        fill="var(--bg2)" stroke="var(--border)" strokeWidth="1"/>
                      {/* Arrow pointing down */}
                      <polygon points={`${dot.x-6},${tipY+tipH} ${dot.x+6},${tipY+tipH} ${dot.x},${dot.y-8}`}
                        fill="var(--bg2)" stroke="var(--border)" strokeWidth="1"/>
                      <polygon points={`${dot.x-5},${tipY+tipH+1} ${dot.x+5},${tipY+tipH+1} ${dot.x},${dot.y-9}`}
                        fill="var(--bg2)"/>
                      <text x={tipX+12} y={tipY+17} fill="var(--text4)" fontSize="8"
                        fontFamily="Montserrat,sans-serif" fontWeight="700" letterSpacing="0.15em">
                        {dot.date}
                      </text>
                      {dot.trades.map((t, ti) => (
                        <g key={ti}>
                          <circle cx={tipX+14} cy={tipY+30+ti*lineH} r="4" fill={t.color}/>
                          <text x={tipX+26} y={tipY+34+ti*lineH} fill="var(--text)"
                            fontSize="10" fontFamily="Montserrat,sans-serif" fontWeight="900">
                            {t.ticker}
                          </text>
                          <text x={tipX+72} y={tipY+34+ti*lineH} fill="var(--text3)"
                            fontSize="10" fontFamily="Montserrat,sans-serif" fontWeight="600">
                            {t.shares?.toFixed(4)}
                          </text>
                          <text x={tipX+tipW-10} y={tipY+34+ti*lineH} textAnchor="end"
                            fill={t.color} fontSize="10" fontFamily="Montserrat,sans-serif" fontWeight="700">
                            {fmt$(t.dollars)}
                          </text>
                        </g>
                      ))}
                    </g>
                  );
                })()}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Asset legend — centred */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:10, justifyContent:"center", alignItems:"center" }}>
        {[...assetData].sort((a,b) => b.currentValue - a.currentValue).map((a, i) => {
          const isHovered = hoveredLegend === a.ticker;
          const isActive  = !hoveredLegend || isHovered;
          return (
            <div key={a.id}
              style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer",
                opacity: isActive ? 1 : 0.25, transition:"opacity 0.2s" }}
              onMouseEnter={() => setHoveredLegend(a.ticker)}
              onMouseLeave={() => setHoveredLegend(null)}
            >
              <div style={{
                width: isHovered ? 10 : 8,
                height: isHovered ? 10 : 8,
                borderRadius:"50%",
                background: COLORS[i % COLORS.length],
                transition:"width 0.15s, height 0.15s",
              }}/>
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
                color: isHovered ? "var(--text)" : isActive ? "var(--text3)" : "var(--text5)",
                transition:"color 0.15s",
              }}>{a.ticker}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default TradeHistoryChart;
